import React, { useState } from 'react';

import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { useAbsoluteNavigate } from '@/hooks/useAbsoluteNavigate';
import { usePrivacyPolicyLoader } from '@/hooks/usePrivacyPolicyLoader';
import { useTermsAndConditionsLoader } from '@/hooks/useTermsAndConditionsLoader';

import {
  selectIsPPConsented,
  selectIsTCConsented,
  selectMandatoryConsents,
  selectUpdateConsentsLoading,
} from '@/store/selectors/consentsSelectors';
import { ConsentView } from '@/models/enum/consent-view.enum';
import { updateConsents } from '@/store/actions/consentsActions';
import { BffStaticTextContentResponse } from '@lucky7ventures/bff-types';
import { ArrowLeftIcon } from '@heroicons/react/20/solid';
import LegalCmsContent from '@/components/shared/LegalCmsContent';
import { BaseModalProps } from '@/features/modal/types';
import Spinner from '@/components/UI/Spinner';

const ConsentsModalTerms = ({
  callback,
  content,
  contentLoading,
}: {
  callback: () => void;
  content: BffStaticTextContentResponse | null;
  contentLoading: boolean;
}) => {
  if (!content) {
    return null;
  }

  return (
    <div className="relative w-full h-full flex flex-col overflow-y-scroll scrollbar-hide px-8 max-w-[750px] max-h-[650px]">
      <div className="flex w-full bg-white sticky top-0 pt-8 pb-4">
        <div className="w-1/4">
          <button type="button" onClick={callback}>
            <ArrowLeftIcon className="h-5 w-5" />
          </button>
        </div>
        <h1 className="!m-0 !text-base w-1/2 text-center">{content.title}</h1>
        <div className="w-1/4" />
      </div>
      <LegalCmsContent data={content} loading={contentLoading} />
    </div>
  );
};

export const ConsentsModal = ({ closeModal }: BaseModalProps): JSX.Element => {
  const [view, setView] = useState<ConsentView>(ConsentView.Consent);
  const absoluteNavigate = useAbsoluteNavigate();
  const { tcData, tcLoading } = useTermsAndConditionsLoader();
  const { ppData, ppLoading } = usePrivacyPolicyLoader();
  const intl = useIntl();
  const isTCConsented = useSelector(selectIsTCConsented);
  const isPPConsented = useSelector(selectIsPPConsented);
  const consents = useSelector(selectMandatoryConsents);
  const isUpdateConsentsLoading = useSelector(selectUpdateConsentsLoading);
  const dispatch = useDispatch();

  const handleSaveConsents = () => {
    const approvedConsents = consents.map(consent => {
      return {
        consentId: consent.consentId,
        version: consent.version,
        consented: true,
      };
    });

    dispatch(updateConsents(approvedConsents));
    closeModal();
  };

  return (
    <div data-cy="consents-modal" className="flex min-h-[400px] rounded-2xl bg-white text-left">
      {view === ConsentView.Consent && (
        <div className="flex flex-col justify-between space-y-4 p-8 max-w-[420px]">
          <div>
            <h1 className="mb-4 text-xl">{intl.formatMessage({ id: 'consentModal.header' })}</h1>
            <p>{intl.formatMessage({ id: 'consentModal.text' })}</p>
            <ul className="mt-4 list-inside list-disc rounded-lg bg-gray-100 p-4 text-sm">
              {!isTCConsented && (
                <li className="mb-0">
                  <button
                    type="button"
                    className="underline hover:no-underline text-left max-w-[90%]"
                    onClick={() => setView(ConsentView.Terms)}
                  >
                    {intl.formatMessage({ id: 'consentModal.termsAndConditions' })}
                  </button>
                </li>
              )}
              {!isPPConsented && (
                <li className="mb-0">
                  <button
                    type="button"
                    className="underline hover:no-underline text-left max-w-[90%]"
                    onClick={() => setView(ConsentView.Privacy)}
                  >
                    {intl.formatMessage({ id: 'consentModal.privacyAndCookieNotice' })}
                  </button>
                </li>
              )}
            </ul>
          </div>
          <div className="mt-6 space-y-4">
            <button
              data-cy="consents-modal-approve"
              className="h-12 w-full rounded-full bg-blue-light px-4 text-center font-bold text-white shadow-lg transition hover:bg-cyan-600 hover:shadow-xl"
              type="button"
              onClick={() => handleSaveConsents()}
            >
              {isUpdateConsentsLoading ? (
                <Spinner width={20} height={20} borderwidth={4} color="white" />
              ) : (
                intl.formatMessage({ id: 'consentModal.approve' })
              )}
            </button>
            <button
              className="h-12 w-full rounded-full bg-gray-200 px-4 text-center font-bold transition hover:bg-gray-300"
              type="button"
              onClick={() => {
                closeModal();
                absoluteNavigate('/logout');
              }}
            >
              {intl.formatMessage({ id: 'consentModal.logout' })}
            </button>
          </div>
        </div>
      )}
      {tcData && view === ConsentView.Terms && (
        <ConsentsModalTerms
          content={tcData}
          callback={() => setView(ConsentView.Consent)}
          contentLoading={tcLoading}
        />
      )}
      {ppData && view === ConsentView.Privacy && (
        <ConsentsModalTerms
          content={ppData}
          callback={() => setView(ConsentView.Consent)}
          contentLoading={ppLoading}
        />
      )}
    </div>
  );
};
